import { useState, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

import { DateTime } from "luxon";
import {
  Typography,
  Box,
  Card,
  Grid2 as Grid,
  Container,
  CardContent,
  CircularProgress,
  useMediaQuery,
  Tabs,
  Tab,
  Fade,
  Chip,
  Badge,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import {
  Visibility,
  CheckCircle,
  Schedule,
  Refresh,
  Download,
  Assessment,
  Timeline,
  History,
} from "@mui/icons-material";

// Import modals
import ScheduledModal from "./modals/ScheduledModal";
import CompletedModal from "./modals/CompletedModal";
import UpcomingNotarizationModal from "./modals/UpcomingNotarizationModal";
import PastNotarizationModal from "./modals/PastNotarizationModal";
import RealTimeRequestModal from "./modals/RealTimeRequestModal";
import ExpiredExpeditedRequestModal from "./modals/ExpiredExpeditedRequestModal";
import { connectWebsocket } from "../../store/actions/websocketActions";
import { fetchNotaryData } from "../../store/actions/notaryActions";

const NotaryJobManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.notarySession.user);
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const realTimeRequests = useSelector(
    (state) => state.notarySession.notaryRequests,
  );
  const navigateToRealRequestsTab = useSelector(
    (state) => state.notarySession.navigateToRealRequestsTab,
  );
  const sessions = useSelector((state) => state.sessions);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Tab management
  const [activeTab, setActiveTab] = useState(0);

  // Get data from Redux state (updated via websocket)
  const upcomingNotarizations = currentUser?.upcoming_notarizations || [];
  const pastNotarizations = currentUser?.past_notarizations || [];
  const expiredExpeditedRequests =
    currentUser?.expired_expedited_requests || [];

  // Modal states
  const [scheduledModalOpen, setScheduledModalOpen] = useState(false);
  const [completedModalOpen, setCompletedModalOpen] = useState(false);
  const [upcomingModalOpen, setUpcomingModalOpen] = useState(false);
  const [pastModalOpen, setPastModalOpen] = useState(false);
  const [realTimeModalOpen, setRealTimeModalOpen] = useState(false);
  const [expiredExpeditedModalOpen, setExpiredExpeditedModalOpen] =
    useState(false);
  const [expiredAcceptConfirmOpen, setExpiredAcceptConfirmOpen] =
    useState(false);
  const [expiredAcceptConfirmJob, setExpiredAcceptConfirmJob] = useState(null);

  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedButton, setSelectedButton] = useState("reminder");

  // Error handling
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [cancelErrorText, setCancelErrorText] = useState("");
  const [rescheduleErrorText, setRescheduleErrorText] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const attemptedDismissRef = useRef(new Set());

  // Countdown timer state - updates every second to force re-render
  const [currentTime, setCurrentTime] = useState(Date.now());

  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  // Update currentTime every second to make countdown timers live
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Ensure notary websocket is connected (safety net in case app-level missed)
  useEffect(() => {
    const ws = sessions?.notarySession;
    if (ws?.isValidated && ws?.token && !ws?.wsConnected) {
      dispatch(connectWebsocket("notarySession", ws.token));
    }
  }, [
    sessions?.notarySession?.wsConnected,
    sessions?.notarySession?.token,
    sessions?.notarySession?.isValidated,
    sessions?.notarySession,
    dispatch,
  ]);

  // Shared DataGrid layout to match MyPermitGrid
  const commonDataGridSx = {
    "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
      outline: "none !important",
    },
    "& .MuiDataGrid-columnHeader:focus-within": {
      outline: "none !important",
    },
    "& .MuiDataGrid-columnHeader": {
      "&:hover": {
        ".MuiDataGrid-menuIcon, .MuiDataGrid-sortIcon, .MuiDataGrid-columnHeader--sorted":
          {
            visibility: "hidden",
            pointerEvents: "none",
          },
        cursor: "default",
      },
      paddingLeft: 1,
      paddingRight: 1,
      margin: 0,
      backgroundColor: "#333",
      color: "#fff",
      borderBottom: "1px solid #ccc",
    },
    "& .MuiDataGrid-columnHeader--sortable": {
      pointerEvents: "none",
    },
    "& .MuiDataGrid-columnHeaders": {
      padding: 0,
      margin: 0,
    },
    "& .MuiDataGrid-columnHeadersInner": {
      width: "100%",
      padding: 0,
    },
    border: "1px solid #FCF2F0",
    "& .MuiDataGrid-cell": {
      borderColor: "#333333",
    },
    "& .MuiDataGrid-columnSeparator": {
      display: "none",
    },
    ".MuiDataGrid-cell:focus-within": {
      outline: "none !important",
    },
    "& .MuiDataGrid-row": {
      cursor: "pointer",
    },
  };

  // Data is fetched once via get_notary_update_object and updated via websocket
  // No need for separate API calls

  // Real-time requests are handled via websocket (new_ron_request event)
  // No need for separate API call

  // Data is loaded via websocket from get_notary_update_object
  // Only set loading state based on whether currentUser exists
  useEffect(() => {
    if (currentUser?.availabilities.length === 0) {
      navigate("/notary/finalize");
      return;
    }
    // Data is already loaded from initial notary session fetch via websocket
    if (currentUser) {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }, [currentUser, navigate]);

  // Listen for Redux action to navigate to real requests tab
  useEffect(() => {
    if (navigateToRealRequestsTab) {
      setActiveTab(2); // Switch to "Real-time Requests" tab
      dispatch({ type: "CLEAR_NAVIGATE_TO_REAL_REQUESTS_TAB" }); // Clear the flag
    }
  }, [navigateToRealRequestsTab, dispatch]);

  // Handle navigation state for auto-selecting tabs
  useEffect(() => {
    if (location.state?.activeTab !== undefined) {
      setActiveTab(location.state.activeTab);

      // If a specific request is selected, open its modal
      if (location.state.selectedRequest) {
        setSelectedJob(location.state.selectedRequest);
        setRealTimeModalOpen(true);
      }

      // Clear the navigation state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await dispatch(fetchNotaryData(currentSession));
      setSuccess("Data refreshed successfully!");
    } catch (error) {
      console.error("Error refreshing notary data:", error);
      setError("Failed to refresh data. Please try again.");
    } finally {
      setRefreshing(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Modal handlers
  const handleUpcomingNotarizationClick = (job) => {
    setSelectedJob(job);
    setUpcomingModalOpen(true);
  };

  const handlePastNotarizationClick = (job) => {
    setSelectedJob(job);
    setPastModalOpen(true);
  };

  const handleRealTimeRequestClick = (request) => {
    setSelectedJob(request);
    setRealTimeModalOpen(true);
  };

  const handleExpiredExpeditedRequestClick = (request) => {
    setSelectedJob(request);
    setExpiredExpeditedModalOpen(true);
  };

  const handleDismissRealTimeRequest = async (requestId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/dismiss-real-time-request`,
        {
          requestId,
          currentSession,
        },
        { withCredentials: true },
      );

      // Remove from Redux state immediately
      dispatch({ type: "DISMISS_RON_REQUEST", payload: requestId });
    } catch (error) {
      // If backend says already gone/not found, remove locally to prevent loops
      const status = error?.response?.status;
      if (status === 400 || status === 404) {
        dispatch({ type: "DISMISS_RON_REQUEST", payload: requestId });
      } else {
        console.error("Error dismissing request:", error);
      }
    }
  };

  const handleRealTimeModalClose = () => {
    setRealTimeModalOpen(false);
    setSelectedJob(null);
  };

  const rejectRequestHandler = () => {
    if (selectedJob?.id) {
      handleDismissRealTimeRequest(selectedJob.id);
    }
  };

  const buildExpiredExpeditedAcceptUrl = (request) => {
    const notaryId = currentUser?.id;
    const instantToken = request?.instant_token;
    const targetId = request?.cst_packageID || request?.task_id;
    const signeeToken =
      request?.signee_reg_token || request?.reg_token || request?.st;
    const signer = request?.signer;
    const expiredRequestId = request?.id;

    if (!notaryId || !instantToken || !targetId || !signeeToken || !signer) {
      return "";
    }

    return `/notary/accept-instant?ni=${encodeURIComponent(notaryId)}&it=${encodeURIComponent(instantToken)}&ti=${encodeURIComponent(targetId)}&st=${encodeURIComponent(signeeToken)}&reason=${encodeURIComponent(signer)}&eid=${encodeURIComponent(expiredRequestId || "")}`;
  };

  const handleAcceptExpiredExpeditedRequest = (request) => {
    const acceptUrl = buildExpiredExpeditedAcceptUrl(request);
    if (!acceptUrl) {
      setError(
        "Unable to open acceptance flow: missing required request parameters.",
      );
      return;
    }

    const newTab = window.open(acceptUrl, "_blank", "noopener,noreferrer");
    if (!newTab) {
      setError(
        "Popup blocked. Please allow popups for this site, then click Accept again.",
      );
      return;
    }

    setSuccess("Opened acceptance flow in a new tab.");
    setExpiredExpeditedModalOpen(false);
    setSelectedJob(null);
  };

  const openExpiredExpeditedAcceptConfirm = (request) => {
    setExpiredAcceptConfirmJob(request);
    setExpiredAcceptConfirmOpen(true);
  };

  const handleConfirmExpiredExpeditedAccept = () => {
    if (!expiredAcceptConfirmJob) return;
    handleAcceptExpiredExpeditedRequest(expiredAcceptConfirmJob);

    const acceptUrl = buildExpiredExpeditedAcceptUrl(expiredAcceptConfirmJob);
    if (acceptUrl) {
      setExpiredAcceptConfirmOpen(false);
      setExpiredAcceptConfirmJob(null);
    }
  };

  const handleRequestAcceptFromExpiredModal = (request) => {
    setExpiredExpeditedModalOpen(false);
    openExpiredExpeditedAcceptConfirm(request);
  };

  const handleAcceptRealTimeRequest = async (requestId) => {
    try {
      // Find the request to get the accept_url
      const request = realTimeRequests.find((req) => req.id === requestId);

      if (!request) {
        setError("Request not found");
        return;
      }

      // Navigate to the accept URL which contains all the necessary parameters
      // This will trigger the actual notarization acceptance flow
      if (request.accept_url) {
        // Remove from Redux state immediately (optimistic update)
        dispatch({ type: "DISMISS_RON_REQUEST", payload: requestId });

        setSuccess("Opening notarization session...");

        // Close modal
        setRealTimeModalOpen(false);
        setSelectedJob(null);

        // Navigate to acceptance page in a new tab
        window.open(request.accept_url, "_blank", "noopener,noreferrer");
      } else {
        setError("Invalid request: missing acceptance URL");
      }
    } catch (error) {
      setError(error.message || "Failed to accept real-time request");
    }
  };

  const handleCompletedCloseModal = () => {
    setCompletedModalOpen(false);
  };

  // Column definitions for different grids
  const upcomingNotarizationColumns = [
    {
      field: "booked_date",
      headerName: "Appointment Time",
      flex: 1,
      renderCell: (params) => {
        const utcDate = DateTime.fromISO(params.value, { zone: "utc" });
        const usDate = utcDate.setZone(currentUser?.timezone);
        const formattedDate = usDate.toFormat("MMMM dd, yyyy hh:mm a");
        return <span>{formattedDate}</span>;
      },
    },
    {
      field: "signer",
      headerName: "Signer",
      flex: 0.8,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {params.row.signer?.fullName}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {params.row.signer?.email}
          </Typography>
        </Box>
      ),
    },
    {
      field: "project_address",
      headerName: "Project Address",
      flex: 1,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.6,
      renderCell: (params) => {
        const bookedDateUtc = DateTime.fromISO(params.row.booked_date, {
          zone: "utc",
        });
        const nowUTC = DateTime.utc();
        const timeDifference = bookedDateUtc - nowUTC;

        let status = "Scheduled";
        let color = "primary";

        if (timeDifference <= 300000) {
          status = "Ready to Start";
          color = "success";
        } else if (timeDifference <= 3600000) {
          // 1 hour
          status = "Starting Soon";
          color = "warning";
        }

        return <Chip label={status} color={color} size="small" />;
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title="View Details">
              <Visibility />
            </Tooltip>
          }
          label="View"
          onClick={() => handleUpcomingNotarizationClick(params.row)}
        />,
      ],
    },
  ];

  const pastNotarizationColumns = [
    {
      field: "completed_date",
      headerName: "Completion Date",
      flex: 1,
      renderCell: (params) => {
        // Get completion date from completed_documents (latest created_at)
        const completedDocs = params.row.completed_documents || [];
        let completionTimestamp = null;

        if (completedDocs.length > 0) {
          // Find the latest document creation timestamp
          const latestDoc = completedDocs.reduce((latest, doc) => {
            return !latest || doc.created_at > latest.created_at ? doc : latest;
          }, null);
          completionTimestamp = latestDoc?.created_at;
        }

        if (!completionTimestamp) {
          return <span>N/A</span>;
        }

        // Convert Unix timestamp to DateTime
        const utcDate = DateTime.fromSeconds(completionTimestamp);
        const usDate = utcDate.setZone(currentUser?.timezone);
        const formattedDate = usDate.toFormat("MMMM dd, yyyy hh:mm a");
        return <span>{formattedDate}</span>;
      },
    },
    {
      field: "signer",
      headerName: "Signer",
      flex: 0.8,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {params.row.signer_details?.first}{" "}
            {params.row.signer_details?.middle}{" "}
            {params.row.signer_details?.last}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {params.row.signer_details?.email}
          </Typography>
        </Box>
      ),
    },
    {
      field: "project_address",
      headerName: "Project Address",
      flex: 1,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.6,
      renderCell: (params) => {
        const isCompleted = params.row.is_notarized;
        return (
          <Chip
            label={isCompleted ? "Completed" : "Cancelled"}
            color={isCompleted ? "success" : "error"}
            size="small"
          />
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 140,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title="View Details">
              <Visibility />
            </Tooltip>
          }
          label="View"
          onClick={() => handlePastNotarizationClick(params.row)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Download Documents">
              <Download />
            </Tooltip>
          }
          label="Download"
          onClick={() => handleDocumentDownload(params.row)}
        />,
      ],
    },
  ];

  const realTimeRequestColumns = useMemo(
    () => [
      {
        field: "created_at",
        headerName: "Request Time",
        flex: 1,
        renderCell: (params) => {
          const utcDate = DateTime.fromISO(params.value, { zone: "utc" });
          const usDate = utcDate.setZone(currentUser?.timezone);
          const formattedDate = usDate.toFormat("MMMM dd, yyyy hh:mm a");
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <Typography variant="body2" fontWeight={500} noWrap={false}>
                {formattedDate}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "requester",
        headerName: "Requester",
        flex: 2,
        minWidth: 300,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Typography variant="body2" fontWeight={500} noWrap={false}>
              {params.row.requester?.fullName || params.row.requestor || "N/A"}
            </Typography>
            <Typography variant="caption" color="textSecondary" noWrap={false}>
              {params.row.requester?.email || "N/A"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "type",
        headerName: "Type",
        flex: 0.6,
        renderCell: (params) => (
          <Chip
            label={params.value === "immediate" ? "Expedited" : "Later Date"}
            color={params.value === "immediate" ? "error" : "warning"}
            size="small"
          />
        ),
      },
      {
        field: "time_remaining",
        headerName: "Time Left",
        flex: 0.6,
        renderCell: (params) => {
          // Reference currentTime to force re-render every second
          // eslint-disable-next-line no-unused-vars
          const _ = currentTime;

          const createdAt = DateTime.fromISO(params.row.created_at, {
            zone: "utc",
          });
          const expiryTime = createdAt.plus({ minutes: 5 });
          const now = DateTime.utc();
          const timeLeft = expiryTime.diff(now);

          if (timeLeft.milliseconds <= 0) {
            // Auto-dismiss expired requests only once per id
            if (!attemptedDismissRef.current.has(params.row.id)) {
              attemptedDismissRef.current.add(params.row.id);
              setTimeout(() => {
                handleDismissRealTimeRequest(params.row.id);
              }, 0);
            }
            return <Chip label="Expired" color="error" size="small" />;
          }

          const minutesLeft = Math.floor(timeLeft.as("minutes"));
          const secondsLeft = Math.floor(timeLeft.as("seconds") % 60);

          return (
            <Chip
              label={`${minutesLeft}:${secondsLeft.toString().padStart(2, "0")}`}
              color="warning"
              size="small"
            />
          );
        },
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 140,
        getActions: (params) => [
          <GridActionsCellItem
            icon={
              <Tooltip title="View Details">
                <Visibility />
              </Tooltip>
            }
            label="View"
            onClick={() => handleRealTimeRequestClick(params.row)}
          />,
          <GridActionsCellItem
            icon={
              <Tooltip title="Accept Request">
                <CheckCircle />
              </Tooltip>
            }
            label="Accept"
            onClick={() => handleAcceptRealTimeRequest(params.row.id)}
          />,
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentTime, currentUser],
  );

  const expiredRequestColumns = [
    {
      field: "requested_created_at",
      headerName: "Requested At",
      flex: 1,
      renderCell: (params) => {
        const rawValue = params.value;
        const utcDate =
          typeof rawValue === "number"
            ? DateTime.fromSeconds(rawValue, { zone: "utc" })
            : DateTime.fromISO(rawValue || "", { zone: "utc" });

        if (!utcDate.isValid) {
          return <span>N/A</span>;
        }

        const usDate = utcDate.setZone(currentUser?.timezone);
        const formattedDate = usDate.toFormat("MMMM dd, yyyy hh:mm a");
        return <span>{formattedDate}</span>;
      },
    },
    {
      field: "signer",
      headerName: "Signer",
      flex: 0.5,
      renderCell: (params) => {
        const signer = params.value || "N/A";
        return (
          <Chip
            label={signer.charAt(0).toUpperCase() + signer.slice(1)}
            color={signer === "owner" ? "primary" : "secondary"}
            size="small"
          />
        );
      },
    },
    {
      field: "project_address",
      headerName: "Project Address",
      flex: 1,
    },
    {
      field: "elapsed",
      headerName: "Elapsed",
      flex: 0.6,
      renderCell: (params) => {
        const requestedRaw = params.row?.requested_created_at;
        const requested =
          typeof requestedRaw === "number"
            ? DateTime.fromSeconds(requestedRaw, { zone: "utc" })
            : DateTime.fromISO(requestedRaw || "", { zone: "utc" });
        if (!requested.isValid) {
          return <span>N/A</span>;
        }

        const now = DateTime.fromMillis(currentTime, { zone: "utc" });
        const diffMinutes = Math.max(
          0,
          Math.floor(now.diff(requested, "minutes").minutes),
        );
        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;

        return (
          <Chip
            label={`${hours}:${minutes.toString().padStart(2, "0")}`}
            color="warning"
            size="small"
          />
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 160,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title="View Details">
              <Visibility />
            </Tooltip>
          }
          label="View"
          onClick={() => handleExpiredExpeditedRequestClick(params.row)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Accept Request">
              <Schedule />
            </Tooltip>
          }
          label="Accept"
          onClick={() => openExpiredExpeditedAcceptConfirm(params.row)}
        />,
      ],
    },
  ];

  const handleDocumentDownload = async (job) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/download-documents`,
        {
          jobId: job.id,
          currentSession,
        },
        {
          withCredentials: true,
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `notarization_${job.id}_documents.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess("Documents downloaded successfully!");
    } catch (error) {
      setError("Failed to download documents");
    }
  };

  return (
    <>
      {/* Snackbar notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccess("")}>
          {success}
        </Alert>
      </Snackbar>

      {loading && !hasLoadedOnce ? (
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "100vh",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <CircularProgress color="warning" size="10rem" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading your notarization dashboard...
          </Typography>
        </Box>
      ) : (
        <Container maxWidth="xxl" sx={{ py: 3 }}>
          <Grid container spacing={3}>
            {/* Header Section */}
            <Grid size={{ xs: 12 }}>
              <Card sx={{ backgroundColor: "#1976d2", color: "white" }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography
                        variant={mobileSize ? "h5" : "h4"}
                        fontWeight="bold"
                      >
                        Notarization Management Dashboard
                      </Typography>
                      <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                        Manage your appointments, view history, and respond to
                        real-time requests
                      </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Refresh All Data">
                        <IconButton
                          onClick={handleRefresh}
                          disabled={refreshing}
                          sx={{ color: "white" }}
                        >
                          <Refresh
                            sx={{
                              animation: refreshing
                                ? "spin 1s linear infinite"
                                : "none",
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Main Content Tabs */}
            <Grid size={{ xs: 12 }}>
              <Card>
                <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant={mobileSize ? "scrollable" : "standard"}
                    scrollButtons="auto"
                  >
                    <Tab
                      icon={<Assessment />}
                      label="Upcoming Appointments"
                      sx={{ minWidth: mobileSize ? 120 : 160, pt: 2 }}
                    />
                    <Tab
                      icon={<History />}
                      label="Past Notarizations"
                      sx={{ minWidth: mobileSize ? 120 : 160, pt: 2 }}
                    />
                    <Tab
                      icon={
                        <Badge
                          badgeContent={realTimeRequests.length}
                          color="error"
                        >
                          <Timeline />
                        </Badge>
                      }
                      label="Live Requests"
                      sx={{ minWidth: mobileSize ? 120 : 160, pt: 2 }}
                    />
                    <Tab
                      icon={<Schedule />}
                      label="Expired Requests"
                      sx={{ minWidth: mobileSize ? 120 : 160, pt: 2 }}
                    />
                  </Tabs>
                </Box>

                <CardContent sx={{ p: 0 }}>
                  {/* Tab Panel 0: Upcoming Notarizations */}
                  {activeTab === 0 && (
                    <Fade in={true}>
                      <Box sx={{ p: 2 }}>
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, display: "flex", alignItems: "center" }}
                        >
                          <Assessment sx={{ mr: 1 }} />
                          Upcoming Notarization Appointments
                        </Typography>
                        <DataGrid
                          sx={{
                            minHeight: 400,
                            ...commonDataGridSx,
                          }}
                          rows={upcomingNotarizations}
                          columns={upcomingNotarizationColumns}
                          getRowId={(row) => row?.id}
                          initialState={{
                            pagination: {
                              paginationModel: { pageSize: 10 },
                            },
                          }}
                          pageSizeOptions={[5, 10, 25, 50]}
                          disableRowSelectionOnClick
                          rowHeight={34}
                          loading={refreshing}
                        />
                      </Box>
                    </Fade>
                  )}

                  {/* Tab Panel 1: Past Notarizations */}
                  {activeTab === 1 && (
                    <Fade in={true}>
                      <Box sx={{ p: 2 }}>
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, display: "flex", alignItems: "center" }}
                        >
                          <History sx={{ mr: 1 }} />
                          Past Notarization History
                        </Typography>
                        <DataGrid
                          sx={{
                            minHeight: 400,
                            "& .MuiDataGrid-row": {
                              cursor: "default !important",
                              "&:hover": {
                                cursor: "default !important",
                              },
                            },
                            "& .MuiDataGrid-cell": {
                              cursor: "default !important",
                              "&:hover": {
                                cursor: "default !important",
                              },
                            },
                            ...commonDataGridSx,
                          }}
                          rows={pastNotarizations}
                          columns={pastNotarizationColumns}
                          getRowId={(row) => {
                            const bnId = row?.notarization?.notarization?.bn_id;
                            return bnId ? `${row.id}_${bnId}` : row.id;
                          }}
                          initialState={{
                            pagination: {
                              paginationModel: { pageSize: 10 },
                            },
                          }}
                          pageSizeOptions={[5, 10, 25, 50]}
                          disableRowSelectionOnClick
                          rowHeight={60}
                          loading={refreshing}
                        />
                      </Box>
                    </Fade>
                  )}

                  {/* Tab Panel 2: Real-time Requests */}
                  {activeTab === 2 && (
                    <Fade in={true}>
                      <Box sx={{ p: 2 }}>
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, display: "flex", alignItems: "center" }}
                        >
                          <Timeline sx={{ mr: 1 }} />
                          Real-Time Notarization Requests
                          {realTimeRequests.length > 0 && (
                            <Chip
                              label={`${realTimeRequests.length} Active`}
                              color="error"
                              size="small"
                              sx={{ ml: 2 }}
                            />
                          )}
                        </Typography>
                        <DataGrid
                          sx={{
                            ...commonDataGridSx,
                          }}
                          rows={realTimeRequests}
                          columns={realTimeRequestColumns}
                          getRowId={(row) => row?.id}
                          initialState={{
                            sorting: {
                              sortModel: [
                                { field: "created_at", sort: "desc" },
                              ],
                            },
                            pagination: {
                              paginationModel: { pageSize: 10 },
                            },
                          }}
                          pageSizeOptions={[5, 10, 25, 50]}
                          disableRowSelectionOnClick
                          rowHeight={50}
                          loading={refreshing}
                        />
                      </Box>
                    </Fade>
                  )}

                  {/* Tab Panel 3: Expired Requests */}
                  {activeTab === 3 && (
                    <Fade in={true}>
                      <Box sx={{ p: 2 }}>
                        <Typography
                          variant="h6"
                          color="primary"
                          sx={{ mb: 2, display: "flex", alignItems: "center" }}
                        >
                          <Schedule sx={{ mr: 1 }} />
                          Expired Expedited RON Requests
                        </Typography>

                        <DataGrid
                          sx={{
                            minHeight: 400,
                            ...commonDataGridSx,
                          }}
                          rows={expiredExpeditedRequests}
                          columns={expiredRequestColumns}
                          getRowId={(row) => {
                            const taskIdentifier =
                              row?.cst_packageID || row?.task_id || row?.id;
                            return `${taskIdentifier}_${row?.signer || "unknown"}`;
                          }}
                          initialState={{
                            pagination: {
                              paginationModel: { pageSize: 10 },
                            },
                          }}
                          pageSizeOptions={[5, 10, 25, 50]}
                          disableRowSelectionOnClick
                          rowHeight={50}
                          loading={refreshing}
                        />
                      </Box>
                    </Fade>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Modals */}
          <ScheduledModal
            open={scheduledModalOpen}
            cancelErrorText={cancelErrorText}
            setCancelErrorText={setCancelErrorText}
            rescheduleErrorText={rescheduleErrorText}
            setRescheduleErrorText={setRescheduleErrorText}
            setScheduledModalOpen={setScheduledModalOpen}
            onClose={() => setScheduledModalOpen(false)}
            selectedJob={selectedJob}
            setSelectedScheduledJob={setSelectedJob}
            jobTakenText={false}
            confirmLoading={false}
            selectedButton={selectedButton}
            setSelectedButton={setSelectedButton}
            cancelLoading={cancelLoading}
            setCancelLoading={setCancelLoading}
            emailLoading={emailLoading}
            setEmailLoading={setEmailLoading}
          />

          <CompletedModal
            currentUser={currentUser}
            open={completedModalOpen}
            cancelErrorText={cancelErrorText}
            setCancelErrorText={setCancelErrorText}
            rescheduleErrorText={rescheduleErrorText}
            setRescheduleErrorText={setRescheduleErrorText}
            setCompletedModalOpen={setCompletedModalOpen}
            onClose={handleCompletedCloseModal}
            selectedJob={selectedJob}
            setSelectedScheduledJob={setSelectedJob}
            jobTakenText={false}
            confirmLoading={false}
            selectedButton={selectedButton}
            setSelectedButton={setSelectedButton}
            cancelLoading={cancelLoading}
            setCancelLoading={setCancelLoading}
          />

          <UpcomingNotarizationModal
            open={upcomingModalOpen}
            onClose={() => setUpcomingModalOpen(false)}
            selectedJob={selectedJob}
            currentUser={currentUser}
            currentSession={currentSession}
          />

          <PastNotarizationModal
            open={pastModalOpen}
            onClose={() => setPastModalOpen(false)}
            selectedJob={selectedJob}
            currentUser={currentUser}
            currentSession={currentSession}
          />

          <RealTimeRequestModal
            open={realTimeModalOpen}
            onClose={handleRealTimeModalClose}
            selectedJob={selectedJob}
            currentUser={currentUser}
            onAccept={handleAcceptRealTimeRequest}
            rejectRequestHandler={rejectRequestHandler}
          />

          <ExpiredExpeditedRequestModal
            open={expiredExpeditedModalOpen}
            onClose={() => setExpiredExpeditedModalOpen(false)}
            selectedJob={selectedJob}
            currentUser={currentUser}
            onAccept={handleRequestAcceptFromExpiredModal}
          />

          <Dialog
            open={expiredAcceptConfirmOpen}
            onClose={() => setExpiredAcceptConfirmOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Confirm Request Acceptance</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Are you sure you want to accept this expired expedited request?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExpiredAcceptConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleConfirmExpiredExpeditedAccept}
              >
                Yes, Accept Request
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      )}

      <style jsx="true">{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </>
  );
};

export default NotaryJobManagement;
